/**
 * Déconnexion sans perte (WEB1-01, décision 6) : si des saisies de ce compte
 * attendent encore le réseau, la déconnexion les annonce d'abord, propose de
 * les envoyer, et ne les efface qu'après une confirmation explicite. Par
 * défaut elles restent sur l'appareil, dans la base du compte que nul autre
 * compte ne lit, et partent à sa prochaine connexion. Une assistance MyEquip
 * ne garde rien sur le poste : envoyer, ou effacer après confirmation.
 */
import {
  createContext, useCallback, useContext, useEffect, useState, type ReactNode,
} from 'react';
import { setSignOutGuard, useAuth } from '../core/auth.js';
import { t } from '../core/i18n.js';
import { drainQueue, summarizeUnsentWork, type UnsentGroup } from '../offline/sync.js';
import { Banner } from './ui/Feedback.js';
import { FactList } from './ui/FactList.js';
import { Button, ConfirmActionModal, Modal } from './ui.js';

const SignOutContext = createContext<(() => void) | undefined>(undefined);

/** Reste-t-il quelque chose que l'envoi peut faire partir ? (une saisie refusée attend une correction) */
function sendableGroups(groups: readonly UnsentGroup[]): boolean {
  return groups.some((group) => group.kind !== 'refusee');
}

function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = (): void => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}

export function UnsentDialog(props: {
  groups: UnsentGroup[];
  /** Assistance MyEquip : sa base est jetable, rien ne peut attendre une prochaine connexion. */
  support: boolean;
  onClose: () => void;
  onSent: (remaining: UnsentGroup[]) => void;
  onKeep: () => void;
  onErase: () => void;
}): ReactNode {
  const online = useOnline();
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const sendable = sendableGroups(props.groups);

  const send = async (): Promise<void> => {
    setSending(true);
    setFailed(false);
    try {
      // Tous les lots, pas seulement le premier : 60 gestes ou 7 photos partent d'un appui.
      await drainQueue();
    } catch {
      // Base illisible : la liste relue ci-dessous dit ce qui reste.
    } finally {
      const remaining = await summarizeUnsentWork().catch(() => props.groups);
      setSending(false);
      // Encore quelque chose d'envoyable : l'envoi n'est pas allé au bout.
      setFailed(sendableGroups(remaining));
      props.onSent(remaining);
    }
  };

  return (
    <Modal open title={t('auth.deconnexionEnvois.titre')} onClose={sending ? () => undefined : props.onClose}>
      <div className="stack">
        <p>{t('auth.deconnexionEnvois.explication')}</p>
        <FactList
          label={t('auth.deconnexionEnvois.liste')}
          facts={props.groups.map((group) => ({
            label: t(`auth.deconnexionEnvois.natures.${group.kind}`),
            value: group.count,
          }))}
        />
        {sendable && !online ? (
          <Banner tone="warn">{t('auth.deconnexionEnvois.horsLigne')}</Banner>
        ) : failed ? (
          <Banner tone="danger">{t('auth.deconnexionEnvois.echec')}</Banner>
        ) : null}
        {/* Une seule action principale : envoyer quand c'est possible, sinon garder. */}
        {sendable ? (
          <Button large variant={online || props.support ? 'primary' : 'secondary'} disabled={sending || !online}
            onClick={() => void send()}>
            {sending ? t('auth.deconnexionEnvois.envoi') : t('auth.deconnexionEnvois.envoyer')}
          </Button>
        ) : null}
        {props.support ? (
          <p className="muted">{t('auth.deconnexionEnvois.assistanceAide')}</p>
        ) : (
          <>
            <Button large variant={sendable && online ? 'secondary' : 'primary'} disabled={sending}
              onClick={props.onKeep}>
              {t('auth.deconnexionEnvois.garder')}
            </Button>
            <p className="muted">{t('auth.deconnexionEnvois.garderAide')}</p>
          </>
        )}
        <Button variant="ghost" disabled={sending} onClick={props.onErase}>
          {t('auth.deconnexionEnvois.effacer')}
        </Button>
      </div>
    </Modal>
  );
}

/** Branche la déconnexion protégée. À monter une fois, sous l'authentification. */
export function SignOutProvider({ children }: { children: ReactNode }): ReactNode {
  const { logout, me } = useAuth();
  const support = Boolean(me?.support_session);
  const [groups, setGroups] = useState<UnsentGroup[] | undefined>();
  const [erasing, setErasing] = useState(false);

  const request = useCallback(() => {
    void (async () => {
      // Lecture impossible : la déconnexion ordinaire garde tout, rien n'est perdu.
      const unsent = await summarizeUnsentWork().catch(() => []);
      if (unsent.length === 0) {
        await logout();
        return;
      }
      setErasing(false);
      setGroups(unsent);
    })();
  }, [logout]);

  // Toute fin de session d'assistance passe par cet écran, même lancée ailleurs (auth.logout).
  useEffect(() => setSignOutGuard(request), [request]);

  const close = useCallback(() => {
    setGroups(undefined);
    setErasing(false);
  }, []);

  const leave = useCallback((erase: boolean) => {
    close();
    void logout({ erase });
  }, [close, logout]);

  return (
    <SignOutContext.Provider value={request}>
      {children}
      {groups && !erasing ? (
        <UnsentDialog
          groups={groups}
          support={support}
          onClose={close}
          onSent={(remaining) => {
            if (remaining.length === 0) leave(false);
            else setGroups(remaining);
          }}
          onKeep={() => leave(false)}
          onErase={() => setErasing(true)}
        />
      ) : null}
      <ConfirmActionModal
        open={Boolean(groups && erasing)}
        tone="danger"
        title={t('auth.deconnexionEnvois.effacerTitre')}
        consequence={<p>{t('auth.deconnexionEnvois.effacerConsequence')}</p>}
        confirmLabel={t('auth.deconnexionEnvois.effacer')}
        onClose={() => setErasing(false)}
        onConfirm={() => leave(true)}
      />
    </SignOutContext.Provider>
  );
}

/**
 * Geste « Se déconnecter » des menus. Hors du fournisseur (aperçu), la
 * déconnexion ordinaire, qui garde aussi les envois en attente.
 */
export function useSignOut(): () => void {
  const request = useContext(SignOutContext);
  const { logout } = useAuth();
  return useCallback(() => {
    if (request) request();
    else void logout();
  }, [request, logout]);
}
